import { apiService } from './api';
import { getApiConfig } from '../config/api';

const apiConfig = getApiConfig();

// ── Types (mirror C# DTOs) ─────────────────────────────────────────────────

export interface SiteDetailDto {
  siteId: number;
  siteName?: string;
  siteLocation?: string;
  userId?: number;
}

export interface UserDto {
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  role: 'Super Admin' | 'Admin' | 'Viewer' | 'User';
  isActive: boolean;
  accessSites: SiteDetailDto[];
  createdBy?: number;
}

export interface CreateSiteRequestDto {
  siteName: string;
  siteLocation: string;
}

export interface CreateUserRequestDto {
  firstName: string;
  email: string;
  password: string;
  role: UserDto['role'];
  loginType: string;
  timezone?: string;
  siteIds?: number[];
}

export interface CreateUserResponseDto {
  success: boolean;
  message?: string;
  user?: UserDto;
}

export interface UpdateUserRequestDto {
  firstName: string;
  email: string;
  role: UserDto['role'];
  siteIds?: number[];
}

// ── API Service ────────────────────────────────────────────────────────────

export const userManagementApi = {
  // GET /api/User  — all Users (admin)
  getAllUsers: async (): Promise<UserDto[]> => {
    const response = await apiService.request<UserDto[]>(apiConfig.ENDPOINTS.GET_ALL_USERS);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch Users');
    }
    return response.data;
  },

  // GET /api/User/{UserId}
  getUserById: async (UserId: number): Promise<UserDto> => {
    const endpoint = apiConfig.ENDPOINTS.GET_USER_BY_ID.replace(':userId', UserId.toString());
    const response = await apiService.request<UserDto>(endpoint);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch User');
    }
    return response.data;
  },

  // POST /api/User/createUser
  createUser: async (data: CreateUserRequestDto): Promise<CreateUserResponseDto> => {
    const response = await apiService.request<CreateUserResponseDto>(apiConfig.ENDPOINTS.CREATE_USER, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to create User');
    }
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create User');
    }
    return response.data;
  },

  // PUT /api/User/{UserId}
  updateUser: async (UserId: number, data: UpdateUserRequestDto): Promise<boolean> => {
    const endpoint = apiConfig.ENDPOINTS.UPDATE_USER.replace(':userId', UserId.toString());
    const response = await apiService.request<boolean>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!response.success) {
      throw new Error(response.error || 'Failed to update User');
    }
    return response.data ?? false;
  },

  // POST /api/UserManagement/sites
  createSite: async (data: CreateSiteRequestDto): Promise<SiteDetailDto> => {
    const response = await apiService.request<SiteDetailDto>(apiConfig.ENDPOINTS.CREATE_SITE, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to create site');
    }
    return response.data;
  },

  // GET /api/UserManagement/sites
  getSites: async (): Promise<SiteDetailDto[]> => {
    const response = await apiService.request<SiteDetailDto[]>(apiConfig.ENDPOINTS.GET_SITES);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch sites');
    }
    return response.data;
  },

  // DELETE /api/User/{UserId}
  deleteUser: async (UserId: number): Promise<void> => {
    const endpoint = apiConfig.ENDPOINTS.DELETE_USER.replace(':userId', UserId.toString());
    const response = await apiService.request(endpoint, {
      method: 'DELETE',
    });
    if (!response.success) {
      throw new Error(response.error || 'Failed to delete User');
    }
  },
};
